import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import type { SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { Empty } from "@/components/Empty";
import { InfoBox } from "@/components/InfoBox";
import { MotifIcon } from "@/components/MotifIcon";
import { Tooltip } from "@/components/Tooltip";
import { Typography } from "@/components/Typography";
import type { InterludeTypes } from "@/interlude";
import { ApiKeyStatusBadge } from "@/pages/applications/pages/apiKeys/components/ApiKeyStatusBadge";
import { apiKeyTableStyles as styles } from "@/pages/applications/pages/apiKeys/components/apiKeyTableStyles";

type McpApiKey = InterludeTypes["McpApiKey"];

interface McpConnectionTableProps {
  connections: Array<McpApiKey>;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onRevoke: (connection: McpApiKey) => void;
}

const localStyles = {
  tableWrapper: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  /**
   * Takes the width the API key table gives its key column: a connection is
   * told apart by its name, so the name is what gets the room.
   */
  colName: {
    ...styles.colName,
    flex: "0 0 50%",
  },
  emptyContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    padding: "60px 0",
  },
  emptyIcon: {
    fontSize: "64px",
  },
  loadingContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "60px 0",
  },
  errorContainer: {
    padding: "24px 0",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

interface McpConnectionRowProps {
  connection: McpApiKey;
  onRevoke: (connection: McpApiKey) => void;
}

const McpConnectionRow: React.FC<McpConnectionRowProps> =
  function McpConnectionRow({ connection, onRevoke }) {
    const { t } = useTranslation();

    const handleRevoke = useHandler(() => {
      onRevoke(connection);
    });

    return (
      <Box>
        <Box sx={styles.row} data-test="mcp-connection-row">
          <Box sx={localStyles.colName}>
            {/*
             * The name as the MCP server minted it, e.g.
             * `claude.ai-someone@example.com`. Shown whole rather than
             * parsed into a user column: the format belongs to the MCP server,
             * and the full string is what the reader matches against the
             * client they connected.
             */}
            <Tooltip title={connection.name} arrow>
              <Typography variant="body" color={theme.colors.staticFgTitle}>
                {connection.name}
              </Typography>
            </Tooltip>
          </Box>
          {/* No key column: see `McpApiKeySchema`. */}
          <Box sx={styles.colStatus}>
            <ApiKeyStatusBadge status={connection.status} />
          </Box>
          <Box sx={styles.colCreatedAt}>
            <Typography variant="body" color={theme.colors.staticFgNote}>
              {/* Same format as the API key list, for the same reason. */}
              {dayjs(connection.createdAt).format("YYYY-MM-DD HH:mm")}
            </Typography>
          </Box>
          <Box sx={styles.colActions}>
            <Tooltip title={t("mcpConnection.row.revoke", "Revoke")} arrow>
              <Box
                component="button"
                type="button"
                sx={styles.dangerActionButton}
                data-test="revoke-mcp-connection-btn"
                aria-label={t("mcpConnection.row.revoke", "Revoke")}
                onClick={handleRevoke}
              >
                <MotifIcon un-i-motif="bin" />
              </Box>
            </Tooltip>
          </Box>
        </Box>
        <Box sx={styles.divider} />
      </Box>
    );
  };

export const McpConnectionTable: React.FC<McpConnectionTableProps> =
  function McpConnectionTable({
    connections,
    isLoading,
    isError,
    onRetry,
    onRevoke,
  }) {
    const { t } = useTranslation();

    return (
      <Box sx={localStyles.tableWrapper}>
        <Box sx={styles.row}>
          <Box sx={localStyles.colName}>
            <Typography
              variant="body"
              fontWeight={500}
              color={theme.colors.staticFgNote}
            >
              {t("mcpConnection.table.column.name", "Connection")}
            </Typography>
          </Box>
          <Box sx={styles.colStatus}>
            <Typography
              variant="body"
              fontWeight={500}
              color={theme.colors.staticFgNote}
            >
              {t("mcpConnection.table.column.status", "Status")}
            </Typography>
          </Box>
          <Box sx={styles.colCreatedAt}>
            <Typography
              variant="body"
              fontWeight={500}
              color={theme.colors.staticFgNote}
            >
              {t("mcpConnection.table.column.createdAt", "Authorized")}
            </Typography>
          </Box>
          <Box sx={styles.colActions} />
        </Box>
        <Box sx={styles.divider} />

        {isLoading ? (
          <Box sx={localStyles.loadingContainer}>
            <CircularProgress size={32} />
          </Box>
        ) : null}

        {/*
         * A failed load says so instead of falling through to the empty
         * state, which would tell the reader nothing is connected.
         */}
        {!isLoading && isError ? (
          <Box
            sx={localStyles.errorContainer}
            data-test="mcp-connection-table-error"
          >
            <InfoBox
              variant="error"
              title={t(
                "mcpConnection.table.error.title",
                "Could not load your MCP connections",
              )}
              description={t(
                "mcpConnection.table.error.description",
                "This list is not showing the connections this organization has.",
              )}
              action={{
                label: t("mcpConnection.table.error.retry", "Try again"),
                onClick: onRetry,
              }}
            />
          </Box>
        ) : null}

        {!isLoading && !isError && connections.length === 0 ? (
          <Box
            sx={localStyles.emptyContainer}
            data-test="mcp-connection-table-empty"
          >
            <Box sx={localStyles.emptyIcon}>
              <Empty />
            </Box>
            <Typography variant="body" color={theme.colors.staticFgNote}>
              {t(
                "mcpConnection.page.empty",
                "No AI clients have been authorized through Crescendo Lab MCP.",
              )}
            </Typography>
          </Box>
        ) : null}

        {!isLoading
          ? connections.map((connection) => (
              <McpConnectionRow
                key={connection.id}
                connection={connection}
                onRevoke={onRevoke}
              />
            ))
          : null}
      </Box>
    );
  };
